import React, { useEffect, useState } from "react";
import { api, assetUrl, downloadUrl } from "@/lib/api";
import { SUBMISSION_STATUSES } from "@/lib/constants";
import { Mail, MessageSquare, FileText, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminSubmissions() {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [comment, setComment] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [decisionMsg, setDecisionMsg] = useState("");

  const load = async () => {
    const params = statusFilter ? { status: statusFilter } : {};
    const { data } = await api.get("/admin/submissions", { params });
    setList(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const open = async (sid) => {
    const { data } = await api.get(`/admin/submissions/${sid}`);
    setSel(data);
    setNewStatus(data.status);
  };

  const saveStatus = async () => {
    if (!sel) return;
    try {
      const { data } = await api.put(`/admin/submissions/${sel.id}/status`, {
        status: newStatus, send_email: true, message: decisionMsg,
      });
      if (data.email_sent) {
        toast.success(`Status updated. Email sent to ${data.recipient}.`);
      } else {
        toast.error(`Status updated, but the email was NOT sent: ${data.email_error || "unknown error"}`, { duration: 10000 });
      }
      setDecisionMsg("");
      await load();
      open(sel.id);
    } catch (e) {
      toast.error("Could not update status: " + (e.response?.data?.detail || e.message));
    }
  };

  const addComment = async () => {
    if (!sel || !comment.trim()) return;
    try {
      await api.post(`/admin/submissions/${sel.id}/comments`, { comment });
      setComment("");
      open(sel.id);
    } catch {
      toast.error("Could not save comment.");
    }
  };

  return (
    <div data-testid="admin-submissions">
      <h1 className="font-serif text-3xl text-[#001F3F]">Manuscript Submissions</h1>
      <div className="w-10 h-[2px] bg-[#2BA6A6] mt-3 mb-6"></div>

      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <label className="text-xs uppercase tracking-wider text-[#001F3F] font-semibold">Filter:</label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 bg-white border border-gray-200 text-sm font-sans">
          <option value="">All statuses</option>
          {SUBMISSION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200" data-testid="submissions-table">
        <table className="w-full text-sm font-sans">
          <thead className="bg-[#F5F7FA] text-[11px] uppercase tracking-wider text-[#001F3F]">
            <tr>
              <th className="text-left px-4 py-3">ID</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Author</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-500">No submissions yet.</td></tr>
            ) : list.map((s) => (
              <tr key={s.id} className="border-t border-gray-100 hover:bg-[#F5F7FA]/60">
                <td className="px-4 py-3 font-mono text-xs text-[#001F3F]">{s.submission_id}</td>
                <td className="px-4 py-3 max-w-xs truncate text-[#001F3F]">{s.title}</td>
                <td className="px-4 py-3 text-gray-600">{s.corresponding_first_name} {s.corresponding_last_name}</td>
                <td className="px-4 py-3 text-xs uppercase tracking-wider text-[#2BA6A6]">{s.article_type}</td>
                <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                <td className="px-4 py-3 text-xs text-gray-500">{s.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => open(s.id)} className="text-[#2BA6A6] text-xs uppercase tracking-wider hover:text-[#001F3F]" data-testid={`open-sub-${s.submission_id}`}>Open</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-6 overflow-y-auto" data-testid="submission-modal">
          <div className="bg-white max-w-4xl w-full">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-[#001F3F] text-white">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#2BA6A6]">Submission {sel.submission_id}</div>
                <div className="font-serif text-xl mt-1">{sel.title}</div>
              </div>
              <button onClick={() => setSel(null)} aria-label="Close" data-testid="close-modal"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-5">
                <Section title="Corresponding Author">
                  <div className="flex items-start gap-4">
                    {mainAuthorPhoto(sel) && (
                      <img src={assetUrl(mainAuthorPhoto(sel))} alt="" className="w-16 h-16 rounded-full object-cover border border-gray-200 shrink-0" data-testid="sub-corr-photo" />
                    )}
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-[#001F3F]">
                        {[sel.corresponding_first_name, sel.corresponding_middle_name, sel.corresponding_last_name, sel.corresponding_second_last_name].filter(Boolean).join(" ")}
                      </div>
                      <div className="text-sm mt-1">
                        <Mail className="inline w-3 h-3 text-[#2BA6A6]" /> <a href={`mailto:${sel.corresponding_email}`} className="text-[#2BA6A6]">{sel.corresponding_email}</a>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                        <Field label="Phone" value={sel.corresponding_phone} />
                        <Field label="ORCID" value={sel.corresponding_orcid} />
                        <Field label="Affiliation" value={sel.corresponding_affiliation} />
                        <Field label="Department" value={sel.corresponding_department} />
                        <Field label="Institution" value={sel.corresponding_institution} />
                        <Field label="City / Country" value={[sel.corresponding_city, sel.corresponding_country].filter(Boolean).join(", ")} />
                      </div>
                    </div>
                  </div>
                </Section>
                <Section title="Article">
                  <Field label="Type" value={sel.article_type} />
                  <Field label="Specialty" value={sel.main_specialty} />
                  <Field label="Short Title" value={sel.short_title} />
                </Section>
                <Section title="Abstract">
                  <p className="text-sm font-serif text-[#0A1118] leading-relaxed">{sel.abstract || "—"}</p>
                </Section>
                <Section title="Keywords">
                  <div className="flex flex-wrap gap-2">{(sel.keywords || []).map((k, i) => <span key={i} className="text-xs px-2 py-1 bg-[#F5F7FA] border">{k}</span>)}</div>
                </Section>
                <Section title={`Authors (${(sel.authors || []).length})`}>
                  <div className="space-y-3" data-testid="sub-authors">
                    {(sel.authors || []).length === 0 ? <div className="text-xs text-gray-500">No authors entered.</div> :
                      (sel.authors || []).map((a, i) => (
                        <div key={i} className="border border-gray-200 bg-[#F5F7FA]/50 p-3 flex items-start gap-3" data-testid={`sub-author-${i}`}>
                          {a.photo_url ? (
                            <img src={assetUrl(a.photo_url)} alt="" className="w-12 h-12 rounded-full object-cover border border-gray-200 shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-[#001F3F]/10 border border-gray-200 shrink-0 flex items-center justify-center text-[10px] text-[#001F3F]">{a.order || i + 1}</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[#001F3F]">
                              {[a.first_name, a.middle_name, a.last_name, a.second_last_name].filter(Boolean).join(" ")}
                              {a.academic_degree ? <span className="text-gray-500 font-normal">, {a.academic_degree}</span> : null}
                              {a.corresponding ? <span className="text-[#2BA6A6] text-xs"> · *corresponding</span> : null}
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1.5">
                              <Field label="Email" value={a.email} />
                              <Field label="ORCID" value={a.orcid} />
                              <Field label="Affiliation" value={a.affiliation} />
                              <Field label="Department" value={a.department} />
                              <Field label="Institution" value={a.institution} />
                              <Field label="City / Country" value={[a.city, a.country].filter(Boolean).join(", ")} />
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </Section>
                <Section title="Files">
                  {(sel.files || []).length === 0 ? <div className="text-xs text-gray-500">No files</div> :
                    <ul className="text-sm space-y-2">{sel.files.map((f, i) => (
                      <li key={i} className="flex items-center gap-2"><FileText className="w-3 h-3 text-[#2BA6A6]" />
                        <a href={downloadUrl(f.stored_path, f.filename)} className="text-[#001F3F] hover:text-[#2BA6A6] underline" data-testid={`file-dl-${i}`}>{f.kind}: {f.filename}</a>
                      </li>
                    ))}</ul>}
                </Section>
                <Section title="Declarations">
                  <ul className="text-xs space-y-1">
                    {Object.entries(sel.declarations || {}).map(([k, v]) => (
                      <li key={k} className={v ? "text-[#001F3F]" : "text-red-600"}>
                        {v ? "✓" : "✗"} {k.replace(/_/g, " ")}
                      </li>
                    ))}
                  </ul>
                </Section>
                <Section title={<>Editorial Comments <MessageSquare className="inline w-3 h-3 ml-1" /></>}>
                  <ul className="space-y-2 mb-3">
                    {(sel.editorial_comments || []).map((c) => (
                      <li key={c.id} className="bg-[#F5F7FA] border border-gray-200 p-3 text-sm">
                        <div className="text-[10px] uppercase tracking-wider text-[#2BA6A6] mb-1">{c.author} · {c.created_at?.slice(0, 16)}</div>
                        {c.comment}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add internal comment…" className="flex-1 px-3 py-2 border border-gray-200 text-sm" />
                    <button onClick={addComment} className="bg-[#001F3F] text-white px-4 text-xs uppercase tracking-wider">Add</button>
                  </div>
                </Section>
              </div>

              <div className="space-y-4">
                <div className="bg-[#F5F7FA] border border-gray-200 p-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6] mb-3">Update Status</div>
                  <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="w-full px-2 py-2 bg-white border border-gray-200 text-sm mb-3" data-testid="status-select">
                    {SUBMISSION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <textarea value={decisionMsg} onChange={(e) => setDecisionMsg(e.target.value)} rows={4} placeholder="Optional message for the author…" className="w-full px-2 py-2 bg-white border border-gray-200 text-sm mb-3" />
                  <button onClick={saveStatus} className="w-full bg-[#001F3F] text-white py-2 text-xs uppercase tracking-wider hover:bg-[#2BA6A6]" data-testid="save-status-btn">Save &amp; Send Email</button>
                </div>
                <div className="bg-white border border-gray-200 p-5 text-xs space-y-2">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6]">Submission Info</div>
                  <Field label="Created" value={sel.created_at?.slice(0, 19).replace("T", " ")} />
                  <Field label="Updated" value={sel.updated_at?.slice(0, 19).replace("T", " ")} />
                  <Field label="Payment" value={sel.payment_status || "Pending"} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function mainAuthorPhoto(sub) {
  const a = (sub?.authors || []).find((x) => x.photo_url) || (sub?.authors || [])[0];
  return a?.photo_url || "";
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6] mb-2">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-sm text-[#001F3F]">{value || "—"}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    Submitted: "bg-blue-50 text-blue-700 border-blue-200",
    "Under Editorial Review": "bg-amber-50 text-amber-700 border-amber-200",
    "Sent to Peer Review": "bg-amber-50 text-amber-700 border-amber-200",
    "Minor Revision": "bg-purple-50 text-purple-700 border-purple-200",
    "Major Revision": "bg-purple-50 text-purple-700 border-purple-200",
    Accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Rejected: "bg-red-50 text-red-700 border-red-200",
    "In Production": "bg-teal-50 text-teal-700 border-teal-200",
    "Official PDF Draft": "bg-teal-50 text-teal-700 border-teal-200",
    Published: "bg-emerald-100 text-emerald-800 border-emerald-300",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${colors[status] || "bg-gray-50 text-gray-700 border-gray-200"}`}>{status}</span>;
}
